import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  noindex = false,
  jsonLd,
  publishedTime,
  modifiedTime,
  authorName,
}: SEOHeadProps) {
  const jsonLdId = useRef(`seo-jsonld-${crypto.randomUUID()}`);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuy.surf';

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);

    if (keywords) {
      setMeta('keywords', keywords);
    }

    const canon = canonicalUrl || siteUrl;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canon);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canon, true);
    setMeta('og:type', ogType, true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow');
    }

    setMeta('Last-Modified', '2026-06-22', false);

    const existingScript = document.getElementById(jsonLdId.current);
    if (existingScript) {
      existingScript.remove();
    }

    const ld: Record<string, unknown> = jsonLd || {};

    if (ogType === 'article') {
      const articleLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        url: canon,
        datePublished: publishedTime || '2026-06-22',
        dateModified: modifiedTime || '2026-06-22',
        author: authorName
          ? { '@type': 'Person', name: authorName }
          : { '@type': 'Organization', name: 'OOPBuy Spreadsheet' },
        publisher: {
          '@type': 'Organization',
          name: 'OOPBuy Spreadsheet',
          url: siteUrl,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canon,
        },
        ...ld,
      };

      const script = document.createElement('script');
      script.id = jsonLdId.current;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(articleLd);
      document.head.appendChild(script);
    } else if (!jsonLd || Object.keys(jsonLd).length === 0) {
      const webSiteLd = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'OOPBuy Spreadsheet',
        url: siteUrl,
        description,
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://kaspreadsheet.com/search-{search_term}',
          'query-input': 'required name=search_term',
        },
      };

      const script = document.createElement('script');
      script.id = jsonLdId.current;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(webSiteLd);
      document.head.appendChild(script);
    } else {
      const merged = {
        '@context': 'https://schema.org',
        ...jsonLd,
      };

      const script = document.createElement('script');
      script.id = jsonLdId.current;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(merged);
      document.head.appendChild(script);
    }

    return () => {
      const cleanup = document.getElementById(jsonLdId.current);
      if (cleanup) {
        cleanup.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, noindex, jsonLd, publishedTime, modifiedTime, authorName]);

  return null;
}